import React from 'react';
import { X, Mail, Phone, Building2, UserCheck, Clock, Star, MessageSquare, Calendar, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card/Card';
import { Separator } from '../../../components/ui/separator';
import { cn } from '../../../utils/cn';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  type: 'contact' | 'user' | 'ai';
  phone?: string;
  company?: string;
  role?: string;
  department?: string;
  title?: string;
  timezone?: string;
  lastActive?: string;
  isOnline?: boolean;
  // Team/Company relationship data
  teamRelationship?: {
    isTeamMember: boolean;
    role: 'admin' | 'agent' | 'viewer' | 'manager';
    department: string;
    joinedAt: string;
  };
  customerRelationship?: {
    isCustomer: boolean;
    customerSince?: string;
    accountType?: 'free' | 'premium' | 'enterprise';
    lifetimeValue?: number;
    lastPurchase?: string;
    accountManager?: string;
  };
  // Activity stats
  stats?: {
    totalMessages: number;
    responseTime: string;
    lastMessageAt: string;
    conversationsCount: number;
  };
}

interface UserProfileModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

const Avatar: React.FC<{
  src?: string;
  fallback: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  online?: boolean;
}> = ({ src, fallback, className, size = 'md', online }) => {
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg'
  };

  return (
    <div className="relative">
      <div className={cn(
        'rounded-full bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 font-semibold flex items-center justify-center overflow-hidden',
        'dark:from-blue-900 dark:to-purple-900 dark:text-blue-300',
        sizeClasses[size],
        className
      )}>
        {src ? (
          <img src={src} alt="" className="rounded-full object-cover w-full h-full" />
        ) : (
          fallback
        )}
      </div>
      {online !== undefined && (
        <div className={cn(
          "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900",
          online ? "bg-green-500" : "bg-gray-400"
        )} />
      )}
    </div>
  );
};

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ user, isOpen, onClose }) => {
  if (!isOpen || !user) return null;

  const getUserTypeIcon = () => {
    switch (user.type) {
      case 'ai':
        return '🤖';
      case 'user':
        return '👤';
      case 'contact':
        return '📧';
      default:
        return '👤';
    }
  };

  const getUserTypeBadge = () => {
    switch (user.type) {
      case 'ai':
        return <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">AI Assistant</Badge>;
      case 'user':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Team Member</Badge>;
      case 'contact':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Contact</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            User Profile
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Profile Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="flex items-start gap-4">
            <Avatar
              src={user.avatar}
              fallback={user.name.charAt(0).toUpperCase()}
              size="xl"
              online={user.isOnline}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {user.name}
                </h3>
                <span className="text-xl">{getUserTypeIcon()}</span>
              </div>
              {getUserTypeBadge()}
              {user.title && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {user.title}
                </p>
              )}
              {user.company && (
                <div className="flex items-center gap-1 mt-1">
                  <Building2 className="h-3 w-3 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {user.company}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900 dark:text-gray-100">{user.email}</span>
              </div>
              {user.phone ? (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900 dark:text-gray-100">{user.phone}</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-400">Coming soon</span>
                </div>
              )}
              {user.timezone && (
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900 dark:text-gray-100">{user.timezone}</span>
                </div>
              )}
              {user.lastActive && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Last active: {user.lastActive}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Relationship */}
          {user.teamRelationship && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Team Relationship</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Role:</span>
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {user.teamRelationship.role}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Department:</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {user.teamRelationship.department}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Joined:</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {user.teamRelationship.joinedAt}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Customer Relationship */}
          {user.customerRelationship && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Customer Relationship</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Account Type:</span>
                  <Badge className={cn(
                    user.customerRelationship.accountType === 'enterprise' 
                      ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                      : user.customerRelationship.accountType === 'premium'
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                  )}>
                    {user.customerRelationship.accountType}
                  </Badge>
                </div>
                {user.customerRelationship.customerSince && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Customer Since:</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {user.customerRelationship.customerSince}
                    </span>
                  </div>
                )}
                {user.customerRelationship.lifetimeValue && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Lifetime Value:</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      ${user.customerRelationship.lifetimeValue.toLocaleString()}
                    </span>
                  </div>
                )}
                {user.customerRelationship.accountManager ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Account Manager:</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {user.customerRelationship.accountManager}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Account Manager:</span>
                    <span className="text-sm text-gray-400">Coming soon</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Activity Stats */}
          {user.stats && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Activity Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {user.stats.totalMessages}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Total Messages
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {user.stats.conversationsCount}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Conversations
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {user.stats.responseTime}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Avg Response
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {user.stats.lastMessageAt ? new Date(user.stats.lastMessageAt).toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Last Message
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Start Conversation
            </Button>
            {user.type === 'contact' && (
              <Button variant="outline" size="icon">
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 